const hasValueBecameShorter = (oldValue: string, value: string) =>
  !!(oldValue && oldValue.length > value.length);

const removeNonNumericCharacters = (value: string) =>
  value.replace(/[^0-9]/g, "");

const limitYearToFourDigits = (value: string) => value.substring(0, 8);

const hasReachedFullDateFormat = (
  oldValue: string,
  isBackspace: boolean,
): string | boolean =>
  !!(
    oldValue &&
    removeNonNumericCharacters(oldValue).length === 8 &&
    !isBackspace
  );

type FormatMonthDay = {
  char: string;
  numericValue: string;
  isBackspace: boolean;
  value: string;
};

const formatMonthDay = ({
  char,
  numericValue,
  isBackspace,
}: FormatMonthDay) => {
  let formattedValue = "";

  for (let i = 0; i < numericValue.length; i++) {
    const validOneDigitMonth = numericValue[0] !== "0";
    const validOneDigitDay = numericValue[2] !== "0";
    const monthFirstDigit =
      i === 0 && numericValue.length === 1 && validOneDigitMonth;
    const dayFirstDigit =
      i === 2 && numericValue.length === 3 && validOneDigitDay;
    const isSingleDigitDay = dayFirstDigit && (char == "/" || char == " ");
    const isSingleDigitMonth = monthFirstDigit && (char == "/" || char == " ");

    formattedValue += numericValue[i];

    if (!isBackspace && isSingleDigitDay) {
      formattedValue = prependZeroToSingleDigitDay(formattedValue);
    } else if (!isBackspace && isSingleDigitMonth) {
      formattedValue = prependZeroToSingleDigitMonth(formattedValue);
    }
  }

  return formattedValue;
};

const prependZeroToSingleDigitMonth = (formattedValue: string) => {
  return "0" + formattedValue.slice(0, 1) + formattedValue.slice(2);
};

const prependZeroToSingleDigitDay = (formattedValue: string) => {
  return formattedValue.slice(0, 2) + "0" + formattedValue.slice(2);
};

const handleBackspace = (formattedValue: string) =>
  formattedValue.endsWith(" /") || formattedValue.endsWith("0 /")
    ? formattedValue.substring(0, formattedValue.length - 3)
    : formattedValue;

type TransformToFormattedDate = {
  value: string;
  oldValue: string;
};

export const transformToFormattedDate = ({
  value,
  oldValue,
}: TransformToFormattedDate) => {
  const char = value[value.length - 1];
  const isBackspace = hasValueBecameShorter(oldValue, value);

  if (hasReachedFullDateFormat(oldValue, isBackspace)) {
    return oldValue;
  }

  const numericValue = removeNonNumericCharacters(value);
  const truncatedValue = limitYearToFourDigits(numericValue);

  let formattedDate = formatMonthDay({
    char,
    numericValue: truncatedValue,
    isBackspace,
    value,
  });

  if (isBackspace) {
    formattedDate = handleBackspace(formattedDate);
  } else {
    const month = formattedDate.slice(0, 2);
    const formattedMonth = month && month.length == 2 ? month + " / " : month;

    const day = formattedDate.slice(2, 4);
    const formattedDay = day && day.length == 2 ? day + " / " : day;

    const year = formattedDate.slice(4, 8);
    const components = [formattedMonth, formattedDay, year].filter((c) => c);

    formattedDate = components.join("");
  }

  return formattedDate;
};
